
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, User, GraduationCap, Award } from 'lucide-react';
import { TeacherDetailProfile as TeacherDetailProfileType } from '@/hooks/useTeacherDetail';
import { StatusBadge } from '@/components/admin/teacher-profiles/StatusBadge';
import { SpecializedBadges } from '@/components/admin/teacher-profiles/SpecializedBadges';
import { transformSubjects } from '@/components/admin/teacher-profiles/utils/subjectsTransformer';

interface TeacherDetailProfileProps {
  profile: TeacherDetailProfileType;
}

export const TeacherDetailProfile: React.FC<TeacherDetailProfileProps> = ({
  profile,
}) => {
  const transformedSubjects = transformSubjects(profile.subjects);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          ข้อมูลส่วนตัว
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">รหัสครู</label>
            <p className="text-sm font-semibold text-blue-600">{profile.teacher_code}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">สถานะ</label>
            <div className="mt-1">
              <StatusBadge status={profile.status} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">ชื่อ-นามสกุล</label>
            <p className="text-sm">{profile.full_name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">ชื่อเล่น</label>
            <p className="text-sm">{profile.nickname || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <Phone className="h-4 w-4" />
              เบอร์โทร
            </label>
            <p className="text-sm">{profile.phone_number || '-'}</p>
          </div>
        </div>

        {/* Experience */}
        <div>
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <GraduationCap className="h-4 w-4" />
            ประสบการณ์การสอน
          </label>
          <p className="text-sm mt-1 whitespace-pre-wrap">
            {profile.teaching_experience || '-'}
          </p>
        </div>

        {/* Academic Achievements */}
        <div>
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <Award className="h-4 w-4" />
            ผลงานวิชาการ
          </label>
          <p className="text-sm mt-1 whitespace-pre-wrap">
            {profile.academic_achievements || '-'}
          </p>
        </div>

        {/* Specialized */}
        <div>
          <label className="text-sm font-medium text-gray-700">ความเชี่ยวชาญเฉพาะทาง</label>
          <div className="mt-1">
            <SpecializedBadges specialized={profile.specialized} />
          </div>
        </div>

        {/* Subjects by Grade Level */}
        <div>
          <label className="text-sm font-medium text-gray-700">วิชาที่สอนแยกตามระดับชั้น</label>
          <div className="mt-2 space-y-4">
            {transformedSubjects.map((gradeData, index) => (
              <div key={index} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">{gradeData.grade_level}</h4>
                <div className="space-y-2">
                  {gradeData.subjects.map((subject, subjectIndex) => (
                    <div key={subjectIndex} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {subject.subject}
                      </Badge>
                      <div className="flex gap-1">
                        {subject.course_types.map((courseType, typeIndex) => (
                          <span
                            key={typeIndex}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                              courseType === 'Inter'
                                ? 'bg-blue-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}
                          >
                            {courseType === 'Inter' ? 'I' : 'R'}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
